import {
  MasonryEngineNodeAction,
  MasonryEngineActionHandler,
} from '@leagueplatform/masonry-engine';
import { MasonryDriverAction } from '@leagueplatform/masonry-renderers';
import {
  MasonryNodeAnalytics,
  postDataProcessor,
} from '@leagueplatform/masonry-api';
import { captureError } from '@leagueplatform/observability';
import {
  DataProcessorField,
  DataProcessorResponse,
  SUCCESS_FIELD_ID,
} from 'types/data-processor.types';
import { getFieldValue } from 'utils/data-processor.utils';
import {
  linkClinicGenericErrorResult,
  linkClinicLinkFailedResult,
  linkClinicSuccessResult,
  showLinkClinicResultModal,
} from './link-clinic-result-modal';

export type MasonryLinkClinicDataProcessorAction = MasonryEngineNodeAction<
  'invokeDataProcessor',
  {
    dataProcessorId: string;
    fields?: DataProcessorField[];
    suppressErrorModal?: boolean;
  },
  MasonryNodeAnalytics,
  DataProcessorResponse
>;

/**
 * Masonry action handler for linking a clinic. Calls the linkClinic data
 * processor and shows a result modal on each terminal outcome:
 *
 * - success                                  -> success modal (deeplink + dismiss)
 * - response.status === 'error' | 'warning'  -> link-failed modal (single dismiss)
 * - network / fetch failure                  -> generic error modal + captureError
 *
 * The actual modal payload is built by `link-clinic-result-modal.ts`; this
 * handler is just orchestration over the data processor result.
 */
export const LinkClinicDataProcessorActionHandler: MasonryEngineActionHandler<
  MasonryLinkClinicDataProcessorAction,
  MasonryDriverAction
> = async (action, { messagingController }) => {
  const { dataProcessorId, fields, suppressErrorModal } = action.payload;

  const showError = (
    logMessage: string,
    cause: unknown,
    response?: DataProcessorResponse,
  ) => {
    captureError(
      new Error(`LinkClinicDataProcessorActionHandler: ${logMessage}`, {
        cause,
      }),
    );
    if (!suppressErrorModal) {
      showLinkClinicResultModal(
        messagingController,
        linkClinicGenericErrorResult(),
      );
    }
    return {
      isSuccess: false as const,
      error: response?.errors || response?.message,
      data: response,
    };
  };

  let response: DataProcessorResponse;
  try {
    response = await postDataProcessor({ dataProcessorId, fields });
  } catch (error) {
    return showError('Failed to invoke data processor', error);
  }

  const isLinkingSuccess =
    getFieldValue(response.fields, SUCCESS_FIELD_ID) === true;

  if (
    response.status === 'error' ||
    response.status === 'warning' ||
    !isLinkingSuccess
  ) {
    showLinkClinicResultModal(
      messagingController,
      linkClinicLinkFailedResult(),
    );
    return { isSuccess: false as const, data: response };
  }

  const clinicName = getFieldValue(fields, 'clinicName');
  showLinkClinicResultModal(
    messagingController,
    linkClinicSuccessResult(clinicName ? String(clinicName) : undefined),
  );

  return { isSuccess: true as const, data: response };
};
